import { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { sampleFlows } from '@/data/sampleFlows';
import { getScreensFromFlow } from '@/types/flow';
import ScreenRenderer from '@/components/preview/ScreenRenderer';
import adeliraLogo from '@/assets/adelira-logo.png';
import { supabase } from '@/integrations/supabase/client';
import { useQuizTracking, getDeviceType, saveUtmParams } from '@/hooks/useQuizTracking';
import { useABTest } from '@/hooks/useABTest';
import { useSheetSync } from '@/hooks/useSheetSync';
import { redirectWithParams } from '@/lib/redirectWithParams';
import { decorateShopUrl } from '@/lib/tracking';
import { initGTM, pushPageOpen, pushFormProgress, pushClickToWebsite, pushElevarUserData, pushElevarCompleteQuiz, SCREEN_STEP_MAP } from '@/lib/gtm';

const FlowPublic = () => {
  const flow = sampleFlows.find(f => f.id === '4')!;
  const rawScreens = getScreensFromFlow(flow);

  const [currentScreen, setCurrentScreen] = useState(0);
  const [direction, setDirection] = useState(1);
  const answersRef = useRef<Record<string, { question: string; answer: string[] }>>({});
  const gtmInitRef = useRef(false);

  const {
    sessionIdRef, startTimeRef, screenTimesRef, backClicksRef, scrollDepthRef,
    recordScreenTime, trackScrollDepth, updateSessionStep, markCompleted, incrementBackClicks,
  } = useQuizTracking();

  const { screens, abVariantRef } = useABTest(rawScreens, sessionIdRef);

  const { syncToSheet } = useSheetSync(
    sessionIdRef, startTimeRef, answersRef, backClicksRef, scrollDepthRef, screenTimesRef, currentScreen,
  );

  // Save UTM params from URL to sessionStorage on first load
  useEffect(() => {
    saveUtmParams();
  }, []);

  // Initialize GTM (only on the public quiz route) and fire page_open
  useEffect(() => {
    if (gtmInitRef.current) return;
    if (!screens[0]?.id) return;
    gtmInitRef.current = true;
    initGTM();
    pushPageOpen();
    pushElevarUserData();
  }, [sessionIdRef, abVariantRef, screens]);

  // Elevar: quiz afgerond zodra het resultaatscherm bereikt is
  useEffect(() => {
    if (screens[currentScreen]?.id === 'scr19') {
      pushElevarCompleteQuiz(abVariantRef.current);
    }
  }, [currentScreen, screens, abVariantRef]);

  // Microsoft Clarity: tag de huidige quiz-stap bij elke stapwissel (SPA)
  useEffect(() => {
    const label = SCREEN_STEP_MAP[screens[currentScreen]?.id ?? '']?.step_label;
    (window as unknown as { clarity?: (...args: unknown[]) => void }).clarity?.(
      'set', 'quiz_step', label ?? String(currentScreen + 1),
    );
  }, [currentScreen, screens]);

  // Track scroll depth on last screen
  useEffect(() => {
    return trackScrollDepth(currentScreen, screens.length);
  }, [currentScreen, screens.length, trackScrollDepth]);

  const handleAnswer = useCallback((stepId: string, questionTitle: string, selectedLabels: string[]) => {
    answersRef.current[stepId] = { question: questionTitle, answer: selectedLabels };
    supabase.from('quiz_answers').insert({
      session_id: sessionIdRef.current,
      question: questionTitle,
      answer: selectedLabels.join(', '),
    }).then(() => {});
  }, []);

  const goNext = useCallback(() => {
    const currentScreenObj = screens[currentScreen];
    // form_progress fires on the button click of the current step
    pushFormProgress(currentScreenObj?.id);

    if (currentScreen < screens.length - 1) {
      recordScreenTime(currentScreen);
      setDirection(1);
      const nextScreen = currentScreen + 1;
      setCurrentScreen(nextScreen);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      updateSessionStep(nextScreen);
      syncToSheet(false, currentScreen + 2);
    }
  }, [currentScreen, screens, syncToSheet, recordScreenTime, updateSessionStep, markCompleted, sessionIdRef]);

  const handleFinalClick = useCallback(() => {
    recordScreenTime(currentScreen);
    markCompleted();
    syncToSheet(true, currentScreen + 1);

    pushClickToWebsite();

    // Stuur quiz-antwoorden naar externe webhook
    const answersPayload = Object.values(answersRef.current).map(({ question, answer }) => ({
      question,
      answer: answer.join(', '),
    }));

    fetch('https://ejepfwphnjtvoegtswjd.supabase.co/functions/v1/quiz-webhook?quiz=mainquiz', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        quiz_name: 'mainquiz',
        session_id: sessionIdRef.current,
        answers: answersPayload,
      }),
    }).catch(() => { /* stille fout — quiz werkt altijd door */ });
  }, [currentScreen, syncToSheet, recordScreenTime, markCompleted]);

  const goBack = useCallback(() => {
    if (currentScreen > 0) {
      recordScreenTime(currentScreen);
      incrementBackClicks();
      setDirection(-1);
      setCurrentScreen(s => s - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [currentScreen, recordScreenTime, incrementBackClicks]);

  // Preload images from next 2 screens
  useEffect(() => {
    const preloadScreens = screens.slice(currentScreen + 1, currentScreen + 3);
    preloadScreens.forEach(scr => {
      scr.steps.forEach(step => {
        const urls: string[] = [];
        if (step.imageUrl) urls.push(step.imageUrl);
        if (step.images) urls.push(...step.images);
        step.options?.forEach(opt => { if (opt.imageUrl) urls.push(opt.imageUrl); });
        urls.forEach(url => { const img = new Image(); img.src = url; });
      });
    });
  }, [currentScreen, screens]);

  const buildShopifyRedirect = useCallback((destinationUrl: string) => {
    const utmContent = abVariantRef.current === 'A' ? 'mainquiz_variant_a' : 'mainquiz_variant_b';

    const withQuizParams = redirectWithParams(destinationUrl, {
      alwaysParams: { utm_quiz: 'mainquiz' },
      missingParams: { utm_content: utmContent },
    });
    return decorateShopUrl(withQuizParams);
  }, [abVariantRef]);

  if (!flow) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <p className="text-gray-500">Flow niet gevonden.</p>
      </div>
    );
  }

  const screen = screens[currentScreen];
  const progress = screens.length > 1 ? ((currentScreen + 1) / screens.length) * 100 : 0;

  // Extract disclaimer step (a3b) to render below button
  const disclaimerStep = screen?.steps.find(s => s.id === 'a3b');
  const stepsWithoutDisclaimer = disclaimerStep
    ? screen?.steps.filter(s => s.id !== 'a3b')
    : screen?.steps;

  const buttonStep = screen?.steps.find(s =>
    ['continue_button', 'submit_button', 'link_button', 'universal_button'].includes(s.type)
  );
  const hasButton = !!buttonStep;
  const hasAutoAdvance = screen?.steps.some(s => s.autoAdvance);
  const hasMultiSelect = screen?.steps.some(s => s.multiSelect);
  const showNextButton = !hasButton && (hasMultiSelect || !hasAutoAdvance);
  const hasLoader = screen?.steps.some(s => s.type === 'loader' || s.type === 'list_loader');
  

  return (
    <div className="min-h-[100dvh] bg-white flex flex-col">
      <Helmet>
        <title>Adelira — Huidverzorging Quiz</title>
        <meta name="description" content="Doe de gratis Adelira huidverzorging quiz en ontdek de routine die past bij jouw huid." />
        <link rel="canonical" href="https://quiz.adelira.nl/" />
        <meta property="og:title" content="Adelira — Huidverzorging Quiz" />
        <meta property="og:description" content="Doe de gratis Adelira huidverzorging quiz en ontdek de routine die past bij jouw huid." />
        <meta property="og:url" content="https://quiz.adelira.nl/" />
      </Helmet>
      {/* Brand header */}
      <div className="h-16 sm:h-20 bg-[#0F1C2E] flex items-center justify-center shrink-0 px-4">
        {flow.id === '4' ? (
          <a
            href={buildShopifyRedirect('https://adelira.nl/products/skinfuse-infusion-kit')}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => {
              supabase.from('quiz_logo_clicks').insert({
                session_id: sessionIdRef.current,
                current_step: currentScreen + 1,
                device_type: getDeviceType(),
              }).then(() => {});
            }}
          >
            <img src={adeliraLogo} alt="Adelira" className="h-12 sm:h-16 object-contain cursor-pointer" width="128" height="64" />
          </a>
        ) : (
          <span className="text-white text-lg font-serif italic tracking-wide">
            {flow.brandName || flow.name}
          </span>
        )}
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-100 shrink-0">
        <motion.div
          className="h-full bg-gray-300"
          initial={{ width: 0 }}
          animate={{ width: `${progress}%` }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
        />
      </div>

      {/* Content */}
      <main className="flex-1 flex flex-col min-h-0">
        <div className="flex-1 overflow-y-auto lg:flex lg:items-start lg:justify-center lg:pt-8">
          <AnimatePresence mode="wait" custom={direction}>
            {screen && (
              <motion.div
                key={screen.id}
                custom={direction}
                initial={{ opacity: 0, x: direction * 60 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction * -60 }}
                transition={{ duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] }}
                className="px-4 py-6 sm:px-6 sm:py-10 md:px-12 lg:px-16 max-w-2xl lg:max-w-3xl mx-auto w-full"
              >
                <ScreenRenderer
                  steps={stepsWithoutDisclaimer || []}
                  onAdvance={goNext}
                  accentColor={flow.accentColor}
                  onAnswer={handleAnswer}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Sticky bottom bar */}
        <div className="shrink-0 px-4 sm:px-6 py-3 pb-[env(safe-area-inset-bottom,12px)] border-t border-gray-100 bg-white">
          <div className="flex flex-col items-center max-w-2xl lg:max-w-3xl mx-auto gap-2">
            {(showNextButton || hasButton) && !hasLoader && (
              <div className="w-full max-w-md">
              {buttonStep?.linkUrl ? (
                  <a
                    href={buildShopifyRedirect(buttonStep.linkUrl)}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={handleFinalClick}
                    className="w-full h-12 sm:h-14 rounded-xl bg-[#0F1C2E] text-white font-semibold text-[17px] tracking-wide flex items-center justify-center gap-2 hover:bg-[#1a2d44] active:scale-[0.98] transition-all"
                  >
                    {buttonStep.title || 'VOLGENDE'}
                    <ArrowRight className="h-4 w-4" />
                  </a>
                ) : (
                  <button
                    onClick={goNext}
                    className="w-full h-12 sm:h-14 rounded-xl bg-[#0F1C2E] text-white font-semibold text-[17px] tracking-wide flex items-center justify-center gap-2 hover:bg-[#1a2d44] active:scale-[0.98] transition-all"
                  >
                    {buttonStep?.title || 'VOLGENDE'}
                    <ArrowRight className="h-4 w-4" />
                  </button>
                )}
              </div>
            )}
            {disclaimerStep && (
              <p className="text-[13px] text-gray-600 text-center leading-snug max-w-sm px-2">
                {disclaimerStep.title.split(/(\[[^\]]+\]\([^)]+\))/g).map((part, i) => {
                  const linkMatch = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
                  if (linkMatch) {
                    return <a key={i} href={decorateShopUrl(linkMatch[2])} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">{linkMatch[1]}</a>;
                  }
                  return part;
                })}
              </p>
            )}
            {currentScreen > 0 && (
              <button
                onClick={goBack}
                className="flex items-center gap-1 text-[14px] text-gray-600 hover:text-gray-800 transition-colors p-1"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Terug
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default FlowPublic;
